import React from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { GO_SHORTCUTS, navItemsFor } from "../lib/nav";
import { modKeyLabel } from "../lib/keys";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";

const Keys = ({ keys }) => (
  <span className="flex items-center gap-1 flex-shrink-0">
    {keys.map((k, i) => (
      <kbd key={k + i} className="kbd">{k}</kbd>
    ))}
  </span>
);

const Row = ({ label, keys }) => (
  <li className="flex items-center justify-between gap-4 py-2 border-b last:border-b-0" style={{ borderColor: "var(--c-border-soft)" }}>
    <span className="text-sm text-slate-700 dark:text-slate-300">{label}</span>
    <Keys keys={keys} />
  </li>
);

/** The keyboard shortcuts, opened with "?" or from the account menu. */
const ShortcutsDialog = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const items = navItemsFor(user);
  const mod = modKeyLabel();
  const goRows = Object.entries(GO_SHORTCUTS)
    .map(([key, to]) => ({ key, item: items.find((i) => i.to === to) }))
    .filter((r) => r.item);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg max-h-[85vh] overflow-y-auto rounded-xl sm:rounded-xl" data-testid="shortcuts-dialog">
        <DialogTitle>{t("shortcuts.title")}</DialogTitle>
        <DialogDescription>{t("shortcuts.description")}</DialogDescription>
        <div className="grid gap-5 sm:grid-cols-2 sm:gap-6">
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{t("shortcuts.general")}</h3>
            <ul>
              <Row label={t("shortcuts.openPalette")} keys={[mod, "K"]} />
              <Row label={t("shortcuts.openPaletteAlt")} keys={["/"]} />
              <Row label={t("shortcuts.newScreening")} keys={["N"]} />
              <Row label={t("shortcuts.showShortcuts")} keys={["?"]} />
              <Row label={t("shortcuts.close")} keys={["Esc"]} />
            </ul>
          </section>
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{t("shortcuts.goTo")}</h3>
            <ul>
              {goRows.map(({ key, item }) => (
                <Row key={key} label={t(item.labelKey)} keys={["G", key.toUpperCase()]} />
              ))}
            </ul>
          </section>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">{t("shortcuts.typingNote")}</p>
      </DialogContent>
    </Dialog>
  );
};

export default ShortcutsDialog;
